import React, { useState } from "react";
import { Popover, Box, Button, FormLabel, useTheme } from "@mui/material";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import dayjs from "dayjs";
import Icon from '../../../components/icon';



const DateRangePopover = ({ initialStartDate, initialEndDate, onDateChange, hasCommunicationGetPermission }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(initialStartDate || null);
  const [endDate, setEndDate] = useState(initialEndDate || null);
  const [selectedFilter, setSelectedFilter] = useState("");

  const predefinedRanges = {
    Today: [dayjs().startOf("day"), dayjs().endOf("day")],
    Yesterday: [
      dayjs().subtract(1, "day").startOf("day"),
      dayjs().subtract(1, "day").endOf("day"),
    ],
    "This Week": [dayjs().startOf("week"), dayjs().endOf("week")],
    "Last Week": [
      dayjs().subtract(1, "week").startOf("week"),
      dayjs().subtract(1, "week").endOf("week"),
    ],
    "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
    "Last Month": [
      dayjs().subtract(1, "month").startOf("day"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
    "Last 3 Months": [
      dayjs().subtract(3, "month").startOf("day"),
      dayjs().endOf("day"),
    ],
  };

  const openPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const handleQuickFilter = (range) => {
    const [start, end] = predefinedRanges[range];
    
    // Cap the end date to today if it's in the future
    const today = dayjs();
    const adjustedEnd = end.isAfter(today) ? today : end;
    
    setStartDate(start);
    setEndDate(adjustedEnd);
    setSelectedFilter(range);
  };

  const isPopoverOpen = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Button 
          variant="outlined" 
          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
          startIcon={<Icon icon='tabler:calendar' />} 
          onClick={openPopover}
          sx={{
            width: { xs: "100%", md: "max-content" },
            backgroundColor: "#fff", 
            border: "1px solid #D9D9D9",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#f5f5f5", 
            },
          }}
        >
          Select Date Range
        </Button>
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box
            sx={{
              padding: 2,
              minWidth: 600,
              display: "flex",
              gap: 2,
            }}
          >
            <Box display="flex" flexDirection='column' gap={2}>
              <Box sx={{display: 'flex'}}>
              <DateRangeCalendar
                value={[startDate, endDate]}
                onChange={(newValue) => {
                  setStartDate(newValue[0]);
                  setEndDate(newValue[1]);
                  setSelectedFilter("");
                }}
                disableFuture
                maxDate={dayjs()}
              />

              </Box>
              <Box sx={{display: 'flex', gap: 18}}>
                <Box sx={{display: 'flex', flexDirection: 'column', ml: 6}}>
                  <FormLabel sx={{ fontWeight: 400, fontSize: '13px', marginBottom: '4px', color:'text.primary', height: '100%' }}>
                    Start Date
                  </FormLabel>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                      setSelectedFilter("");
                    }}
                    disableFuture
                    maxDate={dayjs()} // Set maximum selectable date to today
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        InputProps: {
                          endAdornment: null,
                        },
                      },
                    }}
                    disableOpenPicker
                  />
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', ml:8 }}>
                  <FormLabel sx={{ fontWeight: 400, fontSize: '13px', marginBottom: '4px', color:'text.primary', height: '100%' }}>
                    End Date
                  </FormLabel>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => {
                      setEndDate(newValue);
                      setSelectedFilter("");
                    }}
                    disableFuture
                    maxDate={dayjs()} // Set maximum selectable date to today
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        InputProps: {
                          endAdornment: null,
                        },
                      },
                    }}
                    disableOpenPicker
                  />
                </Box>
              </Box>
            </Box>
            <Box display="flex" flexDirection='column' gap={1} mr={6} mt={4}>
              {Object.keys(predefinedRanges).map((range) => (
                <Button
                  key={range}
                  color={selectedFilter === range ? "main" : "primary"}
                  variant={selectedFilter === range ? "contained" : "text"}
                  onClick={() => handleQuickFilter(range)}
                >
                  {range}
                </Button>
              ))}
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={4} mx={6} my={4}>
            <Button variant="outlined" onClick={closePopover}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color='main'
              onClick={() => {
                onDateChange && onDateChange(dayjs(startDate).format('YYYY-MM-DD'), dayjs(endDate).format('YYYY-MM-DD'));
                closePopover();
              }}
            >
              Apply
            </Button>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePopover;